import { useState, useCallback } from "react";
import type { RegisterFormValues, RegisterFormErrors } from "../auth.types";

const INITIAL_VALUES: RegisterFormValues = {
  email: "",
  password: "",
  repeatPassword: "",
  acceptTerms: false,
};

interface UseRegisterFormOptions {
  onSubmit: (values: RegisterFormValues) => Promise<void>;
}

interface UseRegisterFormReturn {
  values: RegisterFormValues;
  errors: RegisterFormErrors;
  isSubmitting: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCheckboxChange: ({
    name,
    checked,
  }: {
    name: string;
    checked: boolean;
  }) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  setFieldValue: <K extends keyof RegisterFormValues>(
    field: K,
    value: RegisterFormValues[K],
  ) => void;
  clearError: (field: keyof RegisterFormErrors) => void;
}

export const useRegisterForm = ({
  onSubmit,
}: UseRegisterFormOptions): UseRegisterFormReturn => {
  const [values, setValues] = useState<RegisterFormValues>(INITIAL_VALUES);
  const [errors, setErrors] = useState<RegisterFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = useCallback(
    (formValues: RegisterFormValues): RegisterFormErrors => {
      const newErrors: RegisterFormErrors = {};

      if (!formValues.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email)) {
        newErrors.email = "Invalid email address";
      }

      if (!formValues.password.trim()) {
        newErrors.password = "Password is required";
      } else if (formValues.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      }

      if (!formValues.repeatPassword.trim()) {
        newErrors.repeatPassword = "Please confirm your password";
      } else if (formValues.repeatPassword !== formValues.password) {
        newErrors.repeatPassword = "Passwords do not match";
      }

      if (!formValues.acceptTerms) {
        newErrors.acceptTerms = "You must accept the terms and conditions";
      }

      return newErrors;
    },
    [],
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  }, []);

  const handleCheckboxChange = useCallback(
    ({ checked, name }: { checked: boolean; name: string }) => {
      setValues((prev) => ({ ...prev, [name]: checked }));
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    },
    [],
  );

  const setFieldValue = useCallback(
    <K extends keyof RegisterFormValues>(
      field: K,
      value: RegisterFormValues[K],
    ) => {
      setValues((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const clearError = useCallback((field: keyof RegisterFormErrors) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const validationErrors = validate(values);

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setIsSubmitting(true);
      try {
        // implement your logic here

        await onSubmit(values);
      } finally {
        setIsSubmitting(false);
      }
    },
    [values, validate, onSubmit],
  );

  return {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleCheckboxChange,
    handleSubmit,
    setFieldValue,
    clearError,
  };
};
